import { useTranslation } from '../../contexts/TranslationContext';
import type { BillingSubscriptionProduct } from '../../types/billing';
import { replaceBillingTranslationValues } from '../../utils/billingPricing';

type BillingInterval = BillingSubscriptionProduct['interval'];

type BillingPeriodToggleProps = {
  selectedInterval: BillingInterval;
  availableIntervals: BillingInterval[];
  monthsSaved: number;
  onSelectInterval: (interval: BillingInterval) => void;
  className?: string;
  disabled?: boolean;
};

export function BillingPeriodToggle({
  selectedInterval,
  availableIntervals,
  monthsSaved,
  onSelectInterval,
  className = '',
  disabled = false,
}: BillingPeriodToggleProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`mx-auto flex w-full max-w-md gap-1 rounded-full bg-[rgb(var(--theme-surface-strong))] p-1.5 shadow-sm ${className}`}
      role="group"
      aria-label={t('billing.landing.billingPeriod')}
    >
      {(['monthly', 'annual'] as const).map((interval) => {
        const isSelected = selectedInterval === interval;
        const isAvailable = availableIntervals.includes(interval);

        return (
          <button
            key={interval}
            type="button"
            disabled={disabled || !isAvailable}
            aria-pressed={isSelected}
            onClick={() => onSelectInterval(interval)}
            className={`brand-focus-ring relative flex min-h-11 min-w-0 overflow-visible flex-1 flex-row items-center justify-center gap-1 rounded-full border-0 px-2 text-xs font-semibold whitespace-nowrap transition-[background-color,color,box-shadow] duration-200 motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50 min-[360px]:gap-1.5 min-[360px]:text-sm sm:px-4 ${
              isSelected
                ? 'brand-button-primary !border-0 !text-white !transform-none shadow-md'
                : 'bg-[rgb(var(--theme-surface))] text-[rgb(var(--theme-text))] shadow-sm hover:bg-[rgb(var(--theme-primary)/0.08)] hover:text-[rgb(var(--theme-primary-strong))]'
            }`}
          >
            {interval === 'annual' && (
              <span aria-hidden="true" className={`pointer-events-none absolute -top-4 right-1.5 z-10 -rotate-[10deg] rounded-full bg-gradient-to-r from-[rgb(var(--theme-action-primary-bg))] to-[rgb(var(--theme-action-secondary-bg))] px-2 py-1 text-[10px] font-bold leading-none tracking-wide text-white shadow-md ring-2 ring-[rgb(var(--theme-surface-strong))] transition-opacity duration-200 motion-reduce:transition-none ${isAvailable ? 'opacity-100' : 'opacity-0'}`}>
                -25%
              </span>
            )}
            {interval === 'monthly'
              ? t('billing.landing.monthly')
              : t('billing.landing.annual')}
            {interval === 'annual' && monthsSaved > 0 && (
              <span className="relative z-10 whitespace-nowrap rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-800 shadow-sm sm:px-2 sm:text-xs">
                {replaceBillingTranslationValues(t('billing.landing.monthsSaved'), {
                  count: String(monthsSaved),
                })}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
